// Real API integration
const API_URL = process.env.REACT_APP_API_URL;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;

// Common headers for all API requests
const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Token': API_TOKEN
});

/**
 * Transforms API beekeeper data to match the application's expected format
 * @param {Object} beekeeper - Beekeeper data from API
 * @returns {Object} - Transformed beekeeper data
 */
const transformBeekeeperData = (beekeeper) => {
    return {
        id: beekeeper.id,
        name: `${beekeeper.firstName} ${beekeeper.lastName}`,
        imkerei: beekeeper.imkerei,
        adresse: `${beekeeper.address}, ${beekeeper.zip} ${beekeeper.city}`,
        ort: beekeeper.city,
        land: beekeeper.country,
        region: "", // If region comes from the API in the future
        beschreibung: beekeeper.biography || "",
        bilder: beekeeper.profileImageUrl ? [beekeeper.profileImageUrl] : ["/images/placeholder-beekeeper.jpg"],
        bewertung: 5.0, // Default rating if not provided by API
        premium: false, // Set based on API data when available
        produkte: beekeeper.products?.map((product, index) => ({
            id: product.articleNumber || index,
            titel: product.title,
            kurzbeschreibung: product.description || "",
            beschreibung: product.description || "",
            preis: 0, // Set when price is available from API
            menge: product.stock || 0,
            einheit: "",
            kategorie: product.category?.title || ""
        })) || []
    };
};

/**
 * Fetch beekeepers data from the API
 * @param {Object} filters - Optional filters for the request
 * @returns {Promise} - Promise with beekeeper data
 */
export const fetchImkers = (filters = { limit: 20, offset: 0 }) => {
    const requestBody = { filters };

    return fetch(API_URL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestBody)
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            return (data.beekeepers || []).map(transformBeekeeperData);
        });
};

/**
 * Fetch a single beekeeper by ID
 * @param {string} id - Beekeeper ID
 * @returns {Promise} - Promise with beekeeper data
 */
export const fetchImkerById = (id) => {
    return fetch(API_URL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                id: id,
                limit: 1,
                offset: 0
            }
        })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            const beekeeper = data.beekeepers?.[0];
            if (!beekeeper) {
                throw new Error('Imker not found');
            }
            return transformBeekeeperData(beekeeper);
        });
};

/**
 * Search beekeepers with a query
 * @param {string} query - Search query
 * @returns {Promise} - Promise with search results
 */
export const searchImkers = (query) => {
    return fetch(API_URL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                search: query.trim(),
                limit: 20,
                offset: 0
            }
        })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            return (data.beekeepers || []).map(transformBeekeeperData);
        });
};

/**
 * Fetch beekeepers by region
 * @param {string} region - Region name
 * @param {string} land - Country name
 * @returns {Promise} - Promise with filtered beekeepers data
 */
export const fetchImkersByRegion = (region, land) => {
    return fetch(API_URL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                region: region,
                country: land,
                limit: 20,
                offset: 0
            }
        })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            return (data.beekeepers || []).map(transformBeekeeperData);
        });
};

/**
 * Fetch beekeepers by city
 * @param {string} city - City name
 * @returns {Promise} - Promise with filtered beekeepers data
 */
export const fetchImkersByCity = (city) => {
    return fetch(API_URL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                city: city,
                limit: 20,
                offset: 0
            }
        })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            return (data.beekeepers || []).map(transformBeekeeperData);
        });
};

// Mapping von ISO-Ländercodes zu Ländernamen
const countryMapping = {
    'DE': 'Deutschland',
    'AT': 'Österreich',
    'CH': 'Schweiz',
    'NL': 'Niederlande',
    'BE': 'Belgien',
    'FR': 'Frankreich',
    'IT': 'Italien',
    'ES': 'Spanien',
    'PL': 'Polen',
    'CZ': 'Tschechien',
    'DK': 'Dänemark',
    'SE': 'Schweden',
    'NO': 'Norwegen',
    'FI': 'Finnland'
};

const getCountryName = (isoCode) => {
    return countryMapping[isoCode] || isoCode;
};

/**
 * Transforms API location data to match the application's expected format
 * @param {Object} location - Location data from API
 * @returns {Object} - Transformed location data
 */
const transformLocationData = (location) => {
    const countryName = getCountryName(location.country);
    return {
        id: location.id,
        title: location.title,
        street: location.street,
        zip: location.zip,
        city: location.city,
        state: location.state,
        region: countryName,
        country: location.country,
        countryName: countryName,
        locationType: location.locationType,
        openingHours: location.openingHours || {},
        openingHoursNormalized: location.openingHoursNormalized || null,
        alwaysAccessible: Boolean(location.alwaysAccessible),
        paymentAccepted: location.paymentAccepted || [],
        modifiedAt: location.modifiedAt || null,
        closedPeriods: location.closedPeriods || [],
        phone: location.phone,
        description: location.description,
        lat: location.lat,
        lng: location.lng,
        location: location.location,
        beekeeper: {
            id: location.beekeeper.id,
            company: location.beekeeper.company,
            firstName: location.beekeeper.firstName,
            lastName: location.beekeeper.lastName,
            profileImageUrl: location.beekeeper.profileImageUrl,
            description: location.beekeeper.description
        },
        images: location.images || [],
        products: location.products || [],
        categories: location.categories || [],
        otherLocations: location.otherLocations || []
    };
};

const handleResponse = async (response) => {
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return {
        meta: data.meta || { total: 0, limit: 20, offset: 0 },
        locations: (data.locations || []).map(transformLocationData)
    };
};

/**
 * Fetch locations from the API
 * @param {Object} filters - Optional filters for the request
 * @returns {Promise} - Promise with location data
 */
export const fetchLocations = async (filters = {}) => {
    const response = await fetch(API_URL + '/locations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                limit: 20,
                offset: 0,
                ...filters
            }
        })
    });
    return handleResponse(response);
};

/**
 * Fetch a single location by ID
 * @param {number} id - Location ID
 * @returns {Promise} - Promise with location data
 */
export const fetchLocationById = (id) => {
    return fetch(API_URL + '/locations/' + id, {
        method: 'GET',
        headers: getHeaders(),
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            const location = data.location;
            if (!location) {
                throw new Error('Location not found');
            }
            return transformLocationData(location);
        });
};

/**
 * Search locations with a query
 * @param {string} query - Search query
 * @returns {Promise} - Promise with search results
 */
export const searchLocations = async (query) => {
    const response = await fetch(API_URL + '/locations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                search: query.trim(),
                limit: 20,
                offset: 0
            }
        })
    });
    return handleResponse(response);
};

/**
 * Fetch locations by region
 * @param {string} region - Region name
 * @param {string} land - Country name
 * @returns {Promise} - Promise with filtered locations data
 */
export const fetchLocationsByRegion = async (region, land) => {
    const response = await fetch(API_URL + '/locations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                region: region,
                country: land,
                limit: 20,
                offset: 0
            }
        })
    });
    return handleResponse(response);
};

/**
 * Fetch locations by city
 * @param {string} city - City name
 * @returns {Promise} - Promise with filtered locations data
 */
export const fetchLocationsByCity = async (city) => {
    const response = await fetch(API_URL + '/locations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                city: city,
                limit: 20,
                offset: 0
            }
        })
    });
    return handleResponse(response);
};

/**
 * Fetch filtered locations from the API
 * @param {Object} filters - { country, state, city }
 * @returns {Promise} - Promise with filtered locations data
 */
export const fetchLocationsFiltered = async (filters) => {
    const response = await fetch(API_URL + '/locations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            filters: {
                limit: 20,
                offset: 0,
                ...filters
            }
        })
    });
    return handleResponse(response);
};

export const fetchRegions = async (country) => {
    const response = await fetch(API_URL + '/regions?country=' + encodeURIComponent(country), {
        method: 'GET',
        headers: getHeaders()
    });
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    const data = await response.json();
    // Filter out invalid state entries (empty strings, numeric codes like "02")
    if (data.states) {
        data.states = data.states.filter(s => s && s.trim() && !/^\d+$/.test(s.trim()));
    }
    return data;
};

/**
 * Fetch product categories from the API
 * @returns {Promise} - Promise with categories array
 */
export const fetchCategories = async () => {
    const response = await fetch(API_URL + '/categories', {
        method: 'POST',
        headers: getHeaders(),
    });
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data.categories || [];
};

const countryConfig = [
    { iso: 'DE', name: 'Deutschland' },
    { iso: 'AT', name: 'Österreich' },
    { iso: 'CH', name: 'Schweiz' }
];

export const fetchAllRegions = async () => {
    const results = await Promise.all(
        countryConfig.map(async ({ iso, name }) => {
            try {
                const data = await fetchRegions(iso);
                return { name, iso: iso.toLowerCase(), states: data.states || [] };
            } catch {
                return { name, iso: iso.toLowerCase(), states: [] };
            }
        })
    );
    const regions = {};
    results.forEach(({ name, iso, states }) => {
        if (states.length > 0) {
            regions[name] = { iso, states: states.sort() };
        }
    });
    return regions;
};